import React from 'react';
import { Formik, FormikProps } from 'formik';
import { Modal } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { appInfoSelector } from 'Selectors';
import clsx from 'clsx';
import { LIST_TYPES, PCI_LIST_TYPES } from 'Common/enums';
import {
    PCI_VALUE_INPUT,
    GET_TOKEN,
    GET_TOKEN_SUCCESS,
    USE_READ_ONLY_STYLE,
    GET_TOKEN_ERROR,
    MAX_RANGE_LIMIT
} from 'Utils';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import { getAddListEntryFormInitialValues } from '@brighterion/utils/listEntry';
import { getListEntryFormSchema } from 'Routes/ListManagement/schemas';
import styles from './AddListEntryForm.css';
import FormBody from './FormBody/FormBody';

type Props = {
    listType: SingleListType;
    merchantSpecific: boolean;
    handleSubmit: (values: any) => void;
    handleSaveAndContinute: (values: any) => void;
    onClose: () => void;
    isLoading: boolean;
    error: any;
    formikKey: number;
    customListType?: string;
};

function AddListEntryForm({
    listType,
    merchantSpecific,
    handleSubmit,
    handleSaveAndContinute,
    onClose,
    isLoading,
    error,
    formikKey,
    customListType
}: Props) {
    const [tokenIsLoading, setTokenIsLoading] = React.useState(false);
    const formikInnerRef = React.useRef<FormikProps<any>>(null);
    const formContentRef = React.useRef<HTMLDivElement>(null);
    const isSaveAnother = React.useRef<boolean>(false);
    const appInfo = useSelector(appInfoSelector);
    const { t } = useTranslation();
    const isPCIList = PCI_LIST_TYPES.some((v) => v === listType);

    React.useEffect(() => {
        if (isPCIList) {
            const listener = (e: MessageEvent) => {
                // do this to get origin
                const { origin } = new URL(appInfo!.pciComponentsURL);
                if (e.origin !== origin) return;
                if (e?.data) {
                    const pData = e?.data;
                    if (pData?.name === PCI_VALUE_INPUT) {
                        switch (pData?.type) {
                            case GET_TOKEN_SUCCESS: {
                                setTokenIsLoading(false);
                                const values = { ...formikInnerRef.current?.values, value: pData.payload };
                                if (isSaveAnother.current) {
                                    handleSaveAndContinute(values);
                                    isSaveAnother.current = false;
                                } else {
                                    handleSubmit(values);
                                }

                                formikInnerRef.current?.setSubmitting(false);
                                break;
                            }
                            case GET_TOKEN_ERROR: {
                                setTokenIsLoading(false);
                                break;
                            }
                            default:
                                break;
                        }
                    }
                }
            };
            window.addEventListener('message', listener);
            return () => {
                window.removeEventListener('message', listener);
            };
        }

        return () => {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // if PCI LIST then we have to get tokenized value of the value input before submitting
    // else just submit regularly
    const submitForm = (values: any) => {
        if (isPCIList) {
            setTokenIsLoading(true);
            const iframe: HTMLIFrameElement | undefined | null = formContentRef.current?.querySelector(
                'iframe[id="value-input"]'
            );
            iframe?.contentWindow?.postMessage({ type: USE_READ_ONLY_STYLE }, iframe.src);
            iframe?.contentWindow?.postMessage({ type: GET_TOKEN }, iframe.src);
        } else if (isSaveAnother.current) handleSaveAndContinute(values);
        else handleSubmit(values);
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Lists - Add List Entry',
                text: 'Add List Entry'
            }
        });
    };

    return (
        <Formik
            innerRef={formikInnerRef}
            key={formikKey}
            enableReinitialize
            initialValues={getAddListEntryFormInitialValues({ listType, merchantSpecific })}
            validationSchema={getListEntryFormSchema({ customListType, listType, t })}
            onSubmit={submitForm}
        >
            {(formikProps) => {
                const { dirty, isValid } = formikProps;
                const compareListEntryValues =
                    listType === LIST_TYPES.RANGE &&
                    error?.data?.error?.param === 'from' &&
                    formikProps.values.from > formikProps.values.to;
                const maxDiffFromAndTo =
                    listType === LIST_TYPES.RANGE &&
                    +formikProps.values.to - +formikProps.values.from >= MAX_RANGE_LIMIT;

                const disableSave =
                    isLoading || !dirty || !isValid || tokenIsLoading || compareListEntryValues || maxDiffFromAndTo;

                return (
                    <div ref={formContentRef} className={styles.modalContainer}>
                        <div className={clsx(styles.modalBody, styles[listType])}>
                            <FormBody
                                listType={listType}
                                merchantSpecific={merchantSpecific}
                                formikProps={formikProps}
                                error={error}
                            />
                        </div>
                        <Modal.Footer
                            disableOnSubAction={disableSave}
                            disableOnAction={disableSave}
                            disableOnCancel={isLoading || tokenIsLoading}
                            onActionText={t('Save')}
                            onAction={async () => {
                                isSaveAnother.current = false;
                                await formikProps.submitForm();
                                formikProps.setSubmitting(false);
                            }}
                            onSubActionText={t('Save and Add Another')}
                            onSubAction={async () => {
                                isSaveAnother.current = true;
                                await formikProps.submitForm();
                                formikProps.setSubmitting(false);
                            }}
                            onClose={onClose}
                            id='list'
                        >
                            {isLoading && <span className={styles.saving}>{t('savingDots')}</span>}
                        </Modal.Footer>
                    </div>
                );
            }}
        </Formik>
    );
}

export default AddListEntryForm;
